"use client"

import { useEffect, useRef, useState } from "react"
import { ChevronDown } from "lucide-react"

/**
 * Product mega-menu for the marketing header.
 *
 * Pattern lifted from Linear / Stripe / Vercel: a single "Product"
 * trigger that opens a card-style popover listing the home sections
 * each with a one-line description. Lets us add features to the home
 * without saturating the top nav (we already had 5 flat links, was
 * about to grow to 7 with Platform + Compare).
 *
 * Behavior:
 *   - Hover (with a 100ms close delay) on desktop
 *   - Click toggles on touch / keyboard
 *   - ESC closes
 *   - Click-outside closes
 *   - Selecting an item closes immediately (so smooth-scroll on the
 *     landing page kicks in without the menu hiding the destination)
 */

export interface ProductMenuItem {
  label: string
  href: string
  description?: string
}

interface ProductMenuProps {
  label: string
  items: ProductMenuItem[]
  /**
   * Hash links must already be prefixed with "/" if the user is on a
   * non-landing route — caller takes care of resolveHref so this
   * component stays dumb.
   */
  resolveHref?: (href: string) => string
  /**
   * Called BEFORE navigation. Used by the parent to trigger smooth
   * scroll on the landing page or close mobile drawers.
   */
  onItemClick?: (href: string, e: React.MouseEvent) => void
}

export function ProductMenu({
  label,
  items,
  resolveHref = (h) => h,
  onItemClick,
}: ProductMenuProps) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<number | null>(null)

  // Hover-with-delay: opening is instant, closing waits 100ms so the
  // user can move from the trigger to the menu without it disappearing.
  const onEnter = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
    setOpen(true)
  }
  const onLeave = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setOpen(false), 100)
  }

  // Click-outside + ESC to close.
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onClick)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onClick)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`
          group inline-flex items-center gap-1
          px-3 py-1.5 text-sm
          transition-colors duration-150 cursor-pointer
          ${open ? "text-[#0F5132]" : "text-[rgba(15,13,10,0.60)] hover:text-[#0F5132]"}
        `}
      >
        {label}
        <ChevronDown
          size={12}
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Popover. Absolute, anchored to the trigger. The 8px top gap
          reads as a "card detached from the chrome", and the invisible
          padding strip on top extends the hover area so the cursor
          doesn't fall into a dead zone between trigger and panel. */}
      {open && (
        <>
          {/* Hover bridge — invisible padding strip between trigger and
              panel, so the menu doesn't close when the cursor moves
              from one to the other. */}
          <div className="absolute left-0 right-0 top-full h-2" aria-hidden />

          <div
            role="menu"
            className="
              absolute left-0 top-[calc(100%+8px)]
              w-[420px] rounded-2xl overflow-hidden
              bg-white border border-[rgba(10,10,10,0.10)]
              shadow-[0_24px_60px_-24px_rgba(10,10,10,0.20),0_0_0_1px_rgba(255,255,255,0.65)_inset]
              animate-in fade-in slide-in-from-top-1 duration-150 ease-out
            "
          >
            {/* Mint hairline accent — same signature we use on every
                Aperture-Light card so the menu reads as part of the
                same visual system. */}
            <div
              aria-hidden
              className="absolute inset-x-6 top-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(15,81,50,0.45), transparent)",
              }}
            />

            <div className="p-2">
              {items.map((item) => (
                <a
                  key={item.label}
                  href={resolveHref(item.href)}
                  onClick={(e) => {
                    setOpen(false)
                    onItemClick?.(item.href, e)
                  }}
                  role="menuitem"
                  className="
                    group/item flex items-start gap-3 px-3 py-2.5 rounded-xl
                    transition-colors duration-150
                    hover:bg-[rgba(15,81,50,0.04)]
                  "
                >
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-[13.5px] font-semibold text-[rgba(15,13,10,0.85)] group-hover/item:text-[#0F5132] transition-colors"
                    >
                      {item.label}
                    </p>
                    {item.description ? (
                      <p
                        className="mt-0.5 text-[12px] leading-snug text-[rgba(15,13,10,0.50)]"
                      >
                        {item.description}
                      </p>
                    ) : null}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
